import { useState } from "react";
import PptxGenJS from "pptxgenjs";
import { appService } from "../../App/app.service";
import {
  cmToInches,
  parseSlideMasterConfigToObjects,
  extractTitleFromPaperElement,
  renderDomTableAsSlides,
  captureChartElementToSlide,
} from "./pptxUtils";

export function usePPTXExport({ dashboardTitle, fileName, currentQuarter, currentWeekNumber }) {
  const [tooltipText, setTooltipText] = useState("Download as PowerPoint");
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    setLoading(true);
    setTooltipText("Exporting all sections...");

    try {
      const company = JSON.parse(localStorage.getItem("skygeni-user") || '{}')?.company || '';
      const pptx = new PptxGenJS();

      // Fetch and apply master slide config from the backend
      let slideMasterConfigRows = [];
      try {
        const masterConfigApiResponse = await appService.getSlideMasterConfig();
        slideMasterConfigRows = masterConfigApiResponse?.message || [];
      } catch (masterConfigFetchError) {
        console.error('Failed to fetch slide master config:', masterConfigFetchError);
      }

      const {
        slideMasterObjectsList,
        pageNumberConfig: dbPageNumberConfig,
        locationToImageDataMap,
        coverSlideTextList,
        locationToAssetTypeMap,
      } = await parseSlideMasterConfigToObjects(slideMasterConfigRows, company);

      // Always show page numbers; use DB-configured position if present,
      // otherwise position based on which footer side is Text (default: right)
      const isPageNumberOnLeft = locationToAssetTypeMap.Bottom_Left === 'Text' && locationToAssetTypeMap.Bottom_Right !== 'Text';
      const pageNumberOptions = dbPageNumberConfig ?? {
        x: isPageNumberOnLeft ? cmToInches(0.2) : cmToInches(24.0),
        y: cmToInches(13.39),
        w: cmToInches(1.0),
        h: cmToInches(0.6),
        color: '7F7F80',
        fontSize: 6,
        align: isPageNumberOnLeft ? 'left' : 'right',
        valign: 'bottom',
      };

      const slideMasterDefinition = {
        title: 'COLORTOKENS_MASTER',
        background: { color: 'FFFFFF' },
        objects: slideMasterObjectsList,
        slideNumber: pageNumberOptions,
      };
      pptx.defineSlideMaster(slideMasterDefinition);

      // ── Cover slide ────────────────────────────────────────────────────────
      try {
        // Use Coverslide_Logo from DB config (INT_SLIDE_MASTER_CONFIG)
        const coverLogoImageData = locationToImageDataMap.Coverslide_Logo || null;
        const coverSlide = pptx.addSlide();
        coverSlide.background = { color: 'FFFFFF' };

        // Top Right pattern image (aspect-ratio-preserving, max 4.0cm × 2.5cm; larger for C0013)
        if (locationToImageDataMap.Top_Right) {
          const topRightData = locationToImageDataMap.Top_Right;
          const topRightAspect = topRightData.width / topRightData.height;
          const topRightMaxW = company === 'C0013' ? 8.0 : 4.0;
          const topRightMaxH = company === 'C0013' ? 4.5 : 2.5;
          let topRightW = topRightMaxW, topRightH = topRightMaxW / topRightAspect;
          if (topRightH > topRightMaxH) { topRightH = topRightMaxH; topRightW = topRightMaxH * topRightAspect; }
          coverSlide.addImage({
            data: topRightData.dataUrl,
            x: cmToInches(25.4 - topRightW),
            y: 0,
            w: cmToInches(topRightW),
            h: cmToInches(topRightH),
          });
        }

        // Logo (coverslide.png) at top-left, aspect-ratio-preserving
        if (coverLogoImageData?.dataUrl) {
          const logoAspectRatio  = coverLogoImageData.width / coverLogoImageData.height;
          const logoMaxWidthCm   = 7.0;
          const logoMaxHeightCm  = 2.0;
          let logoWidthCm  = logoMaxWidthCm;
          let logoHeightCm = logoMaxWidthCm / logoAspectRatio;
          if (logoHeightCm > logoMaxHeightCm) {
            logoHeightCm = logoMaxHeightCm;
            logoWidthCm  = logoMaxHeightCm * logoAspectRatio;
          }
          coverSlide.addImage({
            data: coverLogoImageData.dataUrl,
            x: cmToInches(0.4),
            y: cmToInches(0.4),
            w: cmToInches(logoWidthCm),
            h: cmToInches(logoHeightCm),
          });
        }

        // Main title
        coverSlide.addText(dashboardTitle || "Regional Sales Review Package", {
          x: cmToInches(0.4),
          y: cmToInches(5.5),
          w: cmToInches(24.6),
          h: cmToInches(1.2),
          fontSize: 32,
          fontFace: "Montserrat ExtraBold",
          align: "center",
          bold: true,
          color: "000000",
        });

        // Week / Quarter subtitle
        const quarterDisplay = currentQuarter || 'Q1';
        const weekDisplay    = currentWeekNumber != null ? currentWeekNumber : 0;
        coverSlide.addText(`Week ${weekDisplay}, ${quarterDisplay}`, {
          x: cmToInches(0.4),
          y: cmToInches(7.5),
          w: cmToInches(24.6),
          h: cmToInches(0.6),
          fontSize: 18,
          fontFace: "Montserrat",
          align: "center",
          color: "000000",
        });

        // Bottom Left pattern image
        if (locationToImageDataMap.Bottom_Left) {
          coverSlide.addImage({
            data: locationToImageDataMap.Bottom_Left.dataUrl,
            x: 0.0,
            y: cmToInches(9.29),
            w: cmToInches(5.5),
            h: cmToInches(5.0),
          });
        }

        // Bottom Right — image or text driven by DB config
        if (locationToAssetTypeMap.Bottom_Right === 'Image' && locationToImageDataMap.Bottom_Right) {
          const bottomRightImageData   = locationToImageDataMap.Bottom_Right;
          const bottomRightMaxWidthCm  = 2.5;
          const bottomRightMaxHeightCm = 0.9;
          const bottomRightImageAspect = bottomRightImageData.width / bottomRightImageData.height;
          let bottomRightWidthCm, bottomRightHeightCm;
          if (bottomRightImageAspect > bottomRightMaxWidthCm / bottomRightMaxHeightCm) {
            bottomRightWidthCm  = bottomRightMaxWidthCm;
            bottomRightHeightCm = bottomRightMaxWidthCm / bottomRightImageAspect;
          } else {
            bottomRightHeightCm = bottomRightMaxHeightCm;
            bottomRightWidthCm  = bottomRightMaxHeightCm * bottomRightImageAspect;
          }
          coverSlide.addImage({
            data: bottomRightImageData.dataUrl,
            x: cmToInches(25.4 - bottomRightWidthCm),
            y: cmToInches(13.30),
            w: cmToInches(bottomRightWidthCm),
            h: cmToInches(bottomRightHeightCm),
          });
        }
        // If locationToAssetTypeMap.Bottom_Right === 'Text', text is included in coverSlideTextList below

        // Footer text entries from DB config
        if (coverSlideTextList.length > 0) {
          coverSlideTextList.forEach(({ text, x, y, w, h, align }) => {
            coverSlide.addText(text, {
              x, y, w, h,
              fontSize: 6,
              color: '7F7F80',
              align: align || 'right',
              valign: 'bottom',
            });
          });
        } else if (locationToAssetTypeMap.Bottom_Right !== 'Image' && locationToAssetTypeMap.Bottom_Left !== 'Image') {
          // Hardcoded fallback — position based on which side is configured as Text (default right)
          const useLeft = locationToAssetTypeMap.Bottom_Left === 'Text';
          coverSlide.addText(`© ${new Date().getFullYear()} ColorTokens, Inc. Confidential and Proprietary`, {
            x: useLeft ? cmToInches(0.5)  : cmToInches(10.0),
            y: cmToInches(13.39),
            w: useLeft ? cmToInches(9.5)  : cmToInches(14.5),
            h: cmToInches(0.6),
            fontSize: 6,
            color: '7F7F80',
            align: useLeft ? 'left' : 'right',
            valign: 'bottom',
          });
        }
      } catch (coverError) {
        console.error('Error creating cover slide:', coverError);
      }

      // ── Content slides ─────────────────────────────────────────────────────
      const allMuiPaperElements  = Array.from(document.querySelectorAll('[class*="MuiPaper-root"]'));
      const topLevelPaperElements = allMuiPaperElements.filter(paper => {
        const parentPaper = paper.parentElement?.closest('[class*="MuiPaper-root"]');
        return !parentPaper || parentPaper === paper;
      });

      let exportedSectionCount = 0;

      for (const paper of topLevelPaperElements) {
        const paperBoundingRect = paper.getBoundingClientRect();
        if (paperBoundingRect.height < 150 || paperBoundingRect.width < 300) continue;

        const paperComputedStyle = window.getComputedStyle(paper);
        if (paperComputedStyle.display === 'none' || paperComputedStyle.visibility === 'hidden') continue;

        if (
          paper.classList.contains('filterContainer') ||
          paper.closest('[class*="filterContainer"]')
        ) continue;

        const paperSlideTitle = extractTitleFromPaperElement(paper) || `Section ${exportedSectionCount + 1}`;

        const visibleTableElements = Array.from(paper.querySelectorAll('table')).filter(
          t => t.offsetParent !== null
        );
        const significantSvgElements = Array.from(paper.querySelectorAll('svg')).filter(
          svg => { const r = svg.getBoundingClientRect(); return r.width > 100 && r.height > 100; }
        );
        const significantCanvasElements = Array.from(paper.querySelectorAll('canvas')).filter(
          c => { const r = c.getBoundingClientRect(); return r.width > 100 && r.height > 100; }
        );

        const paperHasSignificantCharts = significantSvgElements.length > 0 || significantCanvasElements.length > 0;
        const paperHasOnlyTables  = visibleTableElements.length > 0 && !paperHasSignificantCharts;
        const paperHasMixedContent = visibleTableElements.length > 0 && paperHasSignificantCharts;

        const currentPaperSlide = pptx.addSlide({ masterName: 'COLORTOKENS_MASTER' });

        if (paperHasOnlyTables) {
          renderDomTableAsSlides(pptx, currentPaperSlide, paper, paperSlideTitle);
        } else if (paperHasMixedContent) {
          // Hide tables while capturing the chart screenshot
          const hiddenTableContainers = [];
          visibleTableElements.forEach(table => {
            const container = table.closest('[class*="MuiBox-root"]') || table.parentElement;
            if (container) {
              hiddenTableContainers.push({ element: container, originalDisplay: container.style.display });
              container.style.display = 'none';
            }
          });

          await captureChartElementToSlide(currentPaperSlide, paper, paperSlideTitle);

          hiddenTableContainers.forEach(({ element, originalDisplay }) => {
            element.style.display = originalDisplay || '';
          });

          // Add each table on its own slide
          for (const table of visibleTableElements) {
            const muiTableContainer =
              table.closest('[class*="MuiTableContainer"]') ||
              table.closest('[class*="MuiPaper-root"]');
            const tableParentWrapper = muiTableContainer?.parentElement || table.parentElement;

            if (tableParentWrapper) {
              let tableSectionTitle = paperSlideTitle;
              const tableTitleTypographyEl = tableParentWrapper.querySelector('[class*="MuiTypography-h6"]');
              if (tableTitleTypographyEl) {
                const extractedSubTitle = tableTitleTypographyEl.textContent.trim();
                if (extractedSubTitle && extractedSubTitle.length > 2 && extractedSubTitle.length < 100) {
                  tableSectionTitle = extractedSubTitle;
                }
              } else {
                const precedingElementSibling = (muiTableContainer || tableParentWrapper).previousElementSibling;
                if (precedingElementSibling) {
                  const siblingTypographyEl =
                    precedingElementSibling.querySelector('[class*="MuiTypography-"]') ||
                    (precedingElementSibling.classList.contains('MuiTypography-root') ? precedingElementSibling : null);
                  if (siblingTypographyEl) {
                    const extractedSubTitle = siblingTypographyEl.textContent.trim();
                    if (extractedSubTitle && extractedSubTitle.length > 2 && extractedSubTitle.length < 100) {
                      tableSectionTitle = extractedSubTitle;
                    }
                  }
                }
              }

              const tablePageSlide = pptx.addSlide({ masterName: 'COLORTOKENS_MASTER' });
              renderDomTableAsSlides(pptx, tablePageSlide, tableParentWrapper, tableSectionTitle);
            }
          }
        } else {
          await captureChartElementToSlide(currentPaperSlide, paper, paperSlideTitle);
        }

        exportedSectionCount++;
        setTooltipText(`Exporting section ${exportedSectionCount}...`);
      }

      if (exportedSectionCount === 0) throw new Error('No content sections found to export');

      // ── Thank You slide ────────────────────────────────────────────────────
      try {
        const thankYouSlide = pptx.addSlide();
        thankYouSlide.background = { color: 'FFFFFF' };

        // Top Right pattern image (aspect-ratio-preserving, max 4.0cm × 2.5cm; larger for C0013)
        if (locationToImageDataMap.Top_Right) {
          const topRightData = locationToImageDataMap.Top_Right;
          const topRightAspect = topRightData.width / topRightData.height;
          const topRightMaxW = company === 'C0013' ? 8.0 : 4.0;
          const topRightMaxH = company === 'C0013' ? 4.5 : 2.5;
          let topRightW = topRightMaxW, topRightH = topRightMaxW / topRightAspect;
          if (topRightH > topRightMaxH) { topRightH = topRightMaxH; topRightW = topRightMaxH * topRightAspect; }
          thankYouSlide.addImage({
            data: topRightData.dataUrl,
            x: cmToInches(25.4 - topRightW),
            y: 0,
            w: cmToInches(topRightW),
            h: cmToInches(topRightH),
          });
        }

        // Logo at top-left
        const coverLogoImageData = locationToImageDataMap.Coverslide_Logo || null;
        if (coverLogoImageData?.dataUrl) {
          const logoAspectRatio = coverLogoImageData.width / coverLogoImageData.height;
          const logoMaxWidthCm = 7.0;
          const logoMaxHeightCm = 2.0;
          let logoWidthCm = logoMaxWidthCm;
          let logoHeightCm = logoMaxWidthCm / logoAspectRatio;
          if (logoHeightCm > logoMaxHeightCm) {
            logoHeightCm = logoMaxHeightCm;
            logoWidthCm = logoMaxHeightCm * logoAspectRatio;
          }
          thankYouSlide.addImage({
            data: coverLogoImageData.dataUrl,
            x: cmToInches(0.4),
            y: cmToInches(0.4),
            w: cmToInches(logoWidthCm),
            h: cmToInches(logoHeightCm),
          });
        }

        // "Thank You" title
        thankYouSlide.addText("Thank You", {
          x: cmToInches(0.4),
          y: cmToInches(5.0),
          w: cmToInches(24.6),
          h: cmToInches(1.4),
          fontSize: 40,
          fontFace: "Montserrat ExtraBold",
          align: "center",
          bold: true,
          color: "000000",
        });

        // Dashboard title subtitle
        // thankYouSlide.addText(dashboardTitle || "Regional Sales Review Package", {
        //   x: cmToInches(0.4),
        //   y: cmToInches(7.0),
        //   w: cmToInches(24.6),
        //   h: cmToInches(0.8),
        //   fontSize: 20,
        //   fontFace: "Montserrat",
        //   align: "center",
        //   color: "000000",
        // });

        // Bottom Left pattern image
        if (locationToImageDataMap.Bottom_Left) {
          thankYouSlide.addImage({
            data: locationToImageDataMap.Bottom_Left.dataUrl,
            x: 0.0,
            y: cmToInches(9.50),
            w: cmToInches(6.35),
            h: cmToInches(4.826),
          });
        }

        // Bottom Right — image or text
        if (locationToAssetTypeMap.Bottom_Right === 'Image' && locationToImageDataMap.Bottom_Right) {
          const bottomRightImageData = locationToImageDataMap.Bottom_Right;
          const bottomRightMaxWidthCm = 2.5;
          const bottomRightMaxHeightCm = 0.9;
          const bottomRightImageAspect = bottomRightImageData.width / bottomRightImageData.height;
          let bottomRightWidthCm, bottomRightHeightCm;
          if (bottomRightImageAspect > bottomRightMaxWidthCm / bottomRightMaxHeightCm) {
            bottomRightWidthCm = bottomRightMaxWidthCm;
            bottomRightHeightCm = bottomRightMaxWidthCm / bottomRightImageAspect;
          } else {
            bottomRightHeightCm = bottomRightMaxHeightCm;
            bottomRightWidthCm = bottomRightMaxHeightCm * bottomRightImageAspect;
          }
          thankYouSlide.addImage({
            data: bottomRightImageData.dataUrl,
            x: cmToInches(25.4 - bottomRightWidthCm),
            y: cmToInches(13.30),
            w: cmToInches(bottomRightWidthCm),
            h: cmToInches(bottomRightHeightCm),
          });
        }

        // Footer text entries from DB config
        if (coverSlideTextList.length > 0) {
          coverSlideTextList.forEach(({ text, x, y, w, h, align }) => {
            thankYouSlide.addText(text, {
              x, y, w, h,
              fontSize: 6,
              color: '7F7F80',
              align: align || 'right',
              valign: 'bottom',
            });
          });
        } else if (locationToAssetTypeMap.Bottom_Right !== 'Image' && locationToAssetTypeMap.Bottom_Left !== 'Image') {
          const useLeft = locationToAssetTypeMap.Bottom_Left === 'Text';
          thankYouSlide.addText(`© ${new Date().getFullYear()} ColorTokens, Inc. Confidential and Proprietary`, {
            x: useLeft ? cmToInches(0.5) : cmToInches(10.0),
            y: cmToInches(13.39),
            w: useLeft ? cmToInches(9.5) : cmToInches(14.5),
            h: cmToInches(0.6),
            fontSize: 6,
            color: '7F7F80',
            align: useLeft ? 'left' : 'right',
            valign: 'bottom',
          });
        }
      } catch (thankYouError) {
        console.error('Error creating Thank You slide:', thankYouError);
      }

      // ── Save file ──────────────────────────────────────────────────────────
      let exportFileName = fileName;
      if (currentQuarter && currentWeekNumber) {
        exportFileName = `${fileName}_${currentQuarter}_Week${currentWeekNumber}`;
      }

      const exportDateTime = new Date();
      const zeroPadToTwo   = (n) => String(n).padStart(2, '0');
      const formattedDateTime = `${exportDateTime.getFullYear()}-${zeroPadToTwo(exportDateTime.getMonth() + 1)}-${zeroPadToTwo(exportDateTime.getDate())}_${zeroPadToTwo(exportDateTime.getHours())}-${zeroPadToTwo(exportDateTime.getMinutes())}-${zeroPadToTwo(exportDateTime.getSeconds())}`;

      await pptx.writeFile(`${exportFileName}-${formattedDateTime}.pptx`);

      setLoading(false);
      setTooltipText("PowerPoint Downloaded");
      setTimeout(() => setTooltipText("Download as PowerPoint"), 5000);
    } catch (error) {
      console.error('Error exporting to PowerPoint:', error);
      setLoading(false);
      setTooltipText("Export failed. Please try again.");
      setTimeout(() => setTooltipText("Download as PowerPoint"), 5000);
    }
  };

  return { loading, tooltipText, handleExport };
}
